import { useMemo, useState } from 'react';
import { ConfirmationDialogProps } from 'components/ConfirmationDialog/ConfirmationDialog';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { ConfirmationDialogState } from 'features/settings/types/types';
import { useZendesk } from 'features/zendesk/hooks/useZendesk';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';

export const useDeleteAccount = () => {
  const [confirmationDialogState, setConfirmationDialogState] =
    useState<ConfirmationDialogState | null>(null);
  const [enableDelete, setEnableDelete] = useState<boolean>(false);
  const { translation } = useTranslationContext();
  const { fileDeleteAccountRequest } = useZendesk();
  const user = useLoggedInUser();
  const handleDeleteClick = () => {
    setConfirmationDialogState(ConfirmationDialogState.CONFIRM);
  };

  const getFullName = () => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    return `${firstName} ${lastName}`.trim();
  };

  const handleConfirmDelete = async () => {
    try {
      await fileDeleteAccountRequest({ name: getFullName() });
      setConfirmationDialogState(ConfirmationDialogState.SUCCESS);
    } catch (err) {
      setConfirmationDialogState(ConfirmationDialogState.ERROR);
      console.error(err);
    }
  };

  const handleClearDeleteState = () => {
    setConfirmationDialogState(null);
  };

  const handleDeleteErrorBack = () => {
    setConfirmationDialogState(ConfirmationDialogState.CONFIRM);
  };

  const confirmDialogContent = useMemo<ConfirmationDialogProps>(() => {
    const commonProps = {
      PaperProps: {
        sx: {
          maxWidth: '300px',
        },
      },
    };

    switch (confirmationDialogState) {
      case 'CONFIRM':
        return {
          ...commonProps,
          open: confirmationDialogState === ConfirmationDialogState.CONFIRM,
          title: translation('delete_account_question'),
          description: translation('delete_account_type_delete'),
          yesButtonLabel: translation('delete_account_action'),
          noButtonLabel: translation('cancel'),
          handleYes: handleConfirmDelete,
          handleNo: handleClearDeleteState,
          yesButtonProps: {
            color: 'error',
            id: 'delete-account-confirm-button',
            className: 'delete-account__confirm-button',
            disabled: !enableDelete,
          },
          noButtonProps: {
            id: 'delete-account-cancel-button',
          },
        };
      case 'SUCCESS':
        return {
          ...commonProps,
          open: confirmationDialogState === ConfirmationDialogState.SUCCESS,
          title: translation('request_submitted'),
          description: translation('delete_account_in_progress_subheading'),
          yesButtonLabel: translation('general_ok'),
          noButtonLabel: '',
          handleYes: handleClearDeleteState,
          handleNo: handleClearDeleteState,
          yesButtonProps: {
            id: 'delete-account-success-ok-button',
          },
        };
      case 'ERROR':
        return {
          ...commonProps,
          open: confirmationDialogState === ConfirmationDialogState.ERROR,
          title: translation('request_failed'),
          description: translation('generic_failure_error'),
          yesButtonLabel: translation('back'),
          noButtonLabel: translation('cancel'),
          handleYes: handleDeleteErrorBack,
          handleNo: handleClearDeleteState,
          yesButtonProps: {
            id: 'delete-account-failure-back-button',
          },
        };
      default:
        return {
          open: false,
          title: '',
          description: '',
          yesButtonLabel: '',
          noButtonLabel: '',
          handleYes: () => {},
          handleNo: () => {},
        };
    }
  }, [confirmationDialogState, translation, enableDelete]);

  return {
    confirmDialogContent,
    handleDeleteClick,
    setEnableDelete,
    enableDelete,
    confirmationDialogState,
    setConfirmationDialogState,
    handleClearDeleteState,
    handleConfirmDelete,
    handleDeleteErrorBack,
  };
};
